import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppConfig } from '../app-config';
import { APP_CONFIG } from '../app-config.token';
import { IPagingAndSortingParameters } from '../common/interfaces/paging-and-sorting-parameters';
import { CachedHttpClient } from '../common/utils';
import { AddEducationHistoryRecordFilesRequest } from '../contracts/requests/add-education-history-record-files-request';
import { AddWorkerFamilyMemberFilesRequest } from '../contracts/requests/add-worker-family-member-files-request';
import { AssignWorkerToEmployerObjectRequest } from '../contracts/requests/assign-worker-to-employer-object-request';
import { CreateOrUpdateWorkerEducationHistoryRecordRequest } from '../contracts/requests/create-or-update-education-history-record-request';
import { CreateOrUpdateWorkerEmploymentHistoryRecordRequest } from '../contracts/requests/create-or-update-worker-employment-history-record-request';
import { CreateOrUpdateWorkerFamilyMemberRequest } from '../contracts/requests/create-or-update-worker-family-member-request';
import { GetExistingExternalWorkerRequest } from '../contracts/requests/get-existing-external-worker-request';
import { InsureWorkerFamilyMemberRequest } from '../contracts/requests/insure-worker-family-member-request';
import { WorkerProfileSettingsRequest } from '../contracts/requests/worker-profile-settings-request';
import { GetBankAccountNumberConfigurationResponse } from '../contracts/responses/get-bank-account-number-configuration-response';
import { WorkerAddressResponse } from '../contracts/responses/worker-address-response';
import { ApiResult } from '../models/ApiResult';
import { Filter } from '../models/common/filter';
import { CreateWorkerFormPersonalData } from '../models/create-worker-form-personal-data';
import { DictionaryItem } from '../models/dictionary-item';
import { AllInternalWorkersGridDto } from '../models/dtos/AllInternalWorkersGridDto';
import { CompanyDto } from '../models/dtos/CompanyDto';
import { EducationHistoryGridDto } from '../models/dtos/education-history-grid-dto';
import { EducationHistoryRecordDto } from '../models/dtos/education-history-record-dto';
import { EducationHistorySkipInformationDto } from '../models/dtos/education-history/education-history-skip-information-dto';
import { EmploymentHistoryRecordDto } from '../models/dtos/employment-history-record-dto';
import { EmploymentHistorySkipInformationDto } from '../models/dtos/employment-history/employment-history-skip-information-dto';
import { ExternalWorkerGridItemDto } from '../models/dtos/external-worker-grid-item-dto';
import { InternalWorkerAgreementDto } from '../models/dtos/internal-worker-agreement-dto';
import { InternalWorkerDto } from '../models/dtos/internal-worker-dto';
import { LegalizationDocumentDto } from '../models/dtos/legalization-document-files-dto';
import { WorkerContactInfoDto } from '../models/dtos/worker-contact-info-dto';
import { WorkerEmploymentWithWorkedDaysDto } from '../models/dtos/worker-employment-with-worked-days.dto';
import { WorkerFamilyMemberDto } from '../models/dtos/worker-family-member-dto';
import { WorkerFamilyMemberGridDto } from '../models/dtos/worker-family-member-grid-dto';
import { WorkerProfileAgreementsPageConfigDto } from '../models/dtos/worker-profile-agreements-page-config-dto';
import { WorkerProfileDocumentsDto } from '../models/dtos/worker-profile-documents-dto';
import { WorkerSettingsDto } from '../models/dtos/worker-settings-dto';
import { WorkerDataForAgreementFormDto } from '../models/dtos/workers/worker-data-for-agreement-form-dto';
import { ExternalWorker } from '../models/ExternalWorker';
import { FormQuestion } from '../models/forms/form-question';
import { CreateExternalWorkerResult } from '../models/results/CreateExternalWorkerResult';
import { CreateInternalWorkerResult } from '../models/results/CreateInternalWorkerResult';
import { SaveWorkerFilesResponseDto } from '../models/save-worker-files-response-dto';
import { WorkerFileDto } from '../models/worker-file-dto';
import { WorkerFormStatementSummary } from '../models/worker-form-statement-summary';
import { WorkerAddress } from '../models/WorkerAddress';
import { WorkerProfile } from '../models/WorkerProfile';
import { IPagedResult } from '../shared/models/PagedResult';
import { DeregistrateWorkerFamilyMemberInsurance } from './../contracts/requests/deregistrate-worker-family-member-insurance-request';

@Injectable()
export class WorkerService {
  private http = inject(HttpClient);
  private cachedHttp = inject(CachedHttpClient);
  private config = inject<AppConfig>(APP_CONFIG);

  private readonly API_URL = this.config.resourceApiURI;
  private readonly workerPath = '/workers';

  private get workerUrl() {
    return this.API_URL + this.workerPath;
  }

  constructor() { }

  addExternalWorker(externalWorker: ExternalWorker): Observable<CreateExternalWorkerResult> {
    const url = `${this.workerUrl}/externalworker`;
    return this.http.post<CreateExternalWorkerResult>(url, externalWorker);
  }

  addInternalWorker(internalWorker: InternalWorkerDto): Observable<ApiResult<CreateInternalWorkerResult>> {
    const url = `${this.workerUrl}/internalworker`;
    return this.http.post<ApiResult<CreateInternalWorkerResult>>(url, internalWorker);
  }

  addWorkerFormPersonalData(workerPersonalData: CreateWorkerFormPersonalData): Observable<any> {
    const url = `${this.workerUrl}/workerFormPersonalData`;
    return this.http.post<any>(url, workerPersonalData);
  }

  addWorkerAddress(workerFormId: number, workerAdress: WorkerAddress[]): Observable<any> {
    const url = `${this.workerUrl}/workerAddress/${workerFormId}`;
    return this.http.post<any>(url, workerAdress);
  }

  assignWorkerToEmployerObject(workerId: number, request: AssignWorkerToEmployerObjectRequest): Observable<number> {
    const url = `${this.workerUrl}/${workerId}/assignToEmployerObject`;
    return this.http.patch<number>(url, request);
  }

  addWorkerFamilyMemberFiles(workerId: number, memberId: number, request: AddWorkerFamilyMemberFilesRequest) {
    const url = `${this.workerUrl}/${workerId}/familyMembers/${memberId}/files`;
    return this.http.post<SaveWorkerFilesResponseDto>(url, request);
  }

  addEducationHistoryRecordFiles(workerId: number, recordId: number, request: AddEducationHistoryRecordFilesRequest) {
    const url = `${this.workerUrl}/${workerId}/educationHistory/${recordId}/files`;
    return this.http.post<SaveWorkerFilesResponseDto>(url, request);
  }

  createWorkerEmploymentHistoryRecord(workerId: number, request: CreateOrUpdateWorkerEmploymentHistoryRecordRequest): Observable<SaveWorkerFilesResponseDto> {
    const url = `${this.workerUrl}/${workerId}/employmentHistory`;
    return this.http.post<SaveWorkerFilesResponseDto>(url, request);
  }

  createWorkerEducationHistoryRecord(workerId: number, request: CreateOrUpdateWorkerEducationHistoryRecordRequest): Observable<number> {
    const url = `${this.workerUrl}/${workerId}/educationHistory`;
    return this.http.post<number>(url, request);
  }

  createWorkerFamilyMember(workerId: number, request: CreateOrUpdateWorkerFamilyMemberRequest) {
    const url = `${this.workerUrl}/${workerId}/familyMembers`;
    return this.http.post<number>(url, request);
  }

  getInternalWorker(workerId: number): Observable<ApiResult<InternalWorkerDto>> {
    const url = `${this.workerUrl}/internalWorker/${workerId}`;
    return this.http.get<ApiResult<InternalWorkerDto>>(url);
  }

  getInternalWorkerEmployerObjects(workerId: number, page: number, count: number, name: string = ''): Observable<IPagedResult<DictionaryItem>> {
    const url = `${this.workerUrl}/${workerId}/internalWorkerEmployerObjects`;
    const params = new HttpParams()
      .set('page', `${page || 1}`)
      .set('count', `${count || 50}`)
      .set('name', name ?? '');

    return this.http.get<IPagedResult<DictionaryItem>>(url, { params });
  }

  addInternalWorkerEmployerObject(workerId: number, employerObjectId: number): Observable<ApiResult<any>> {
    const url = `${this.workerUrl}/${workerId}/internalWorkerEmployerObjects/${employerObjectId}`;
    return this.http.post<ApiResult<any>>(url, null);
  }

  removeInternalWorkerEmployerObject(workerId: number, employerObjectId: number): Observable<ApiResult<any>> {
    const url = `${this.workerUrl}/${workerId}/internalWorkerEmployerObjects/${employerObjectId}`;
    return this.http.delete<ApiResult<any>>(url);
  }

  deleteWorkerFamilyMember(workerId: number, memberId: number): Observable<any> {
    const url = `${this.workerUrl}/${workerId}/familyMembers/${memberId}`;
    return this.http.delete<any>(url);
  }

  getAllInternalWorkers(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
    filters: Filter[],
  ): Observable<IPagedResult<AllInternalWorkersGridDto>> {
    const url = `${this.workerUrl}/allInternalWorkers`;

    let params = new HttpParams()
      .set('page', `${page || 1}`)
      .set('count', `${count || 10}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection)
      .set('filterString', JSON.stringify(filters));

    const httpOptions = {
      params: params,
    };

    return this.http.get<IPagedResult<AllInternalWorkersGridDto>>(url, httpOptions);
  }

  getAllExternalWorkers(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
    filters: Filter[],
  ): Observable<IPagedResult<ExternalWorkerGridItemDto>> {
    const url = `${this.workerUrl}/allExternalWorkers`;

    let params = new HttpParams().set('page', `${page || 1}`).set('count', `${count || 10}`);

    if (sortingField) {
      params = params.set('sortingField', sortingField).set('sortingDirection', sortingDirection);
    }

    if (filters) {
      params = params.set('filterString', JSON.stringify(filters));
    }

    return this.http.get<IPagedResult<ExternalWorkerGridItemDto>>(url, { params });
  }

  getsubordinatesBasicData(): Observable<DictionaryItem[]> {
    const url = `${this.workerPath}/subordinatesBasicData`;

    return this.cachedHttp.get<DictionaryItem[]>(url).pipe(catchError((err) => of([])));
  }

  getExistingExternalWorker(request: GetExistingExternalWorkerRequest): Observable<ExternalWorker | null> {
    const url = `${this.workerUrl}/existingExternalWorker`;

    let params = new HttpParams()
      .set('pesel', request.Pesel)
      .set('documentSerial', request.DocumentSerial)
      .set('documentNumber', request.DocumentNumber);

    if (!!request.IdentityDocumentTypeId) {
      params = params.set('identityDocumentTypeId', request.IdentityDocumentTypeId.toString());
    }

    return this.http.get<ExternalWorker | null>(url, {
      params: params,
    });
  }

  getWorkerEmploymentHistory(
    workerId: number,
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
  ): Observable<IPagedResult<EmploymentHistoryRecordDto>> {
    let params = new HttpParams()
      .set('page', `${page || 1}`)
      .set('count', `${count || 10}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection);

    const url = `${this.workerUrl}/${workerId}/employmentHistory`;
    return this.http.get<IPagedResult<EmploymentHistoryRecordDto>>(url, { params: params });
  }

  getWorkerEducationHistory(
    workerId: number,
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
  ): Observable<IPagedResult<EducationHistoryGridDto>> {
    let params = new HttpParams()
      .set('page', `${page || 1}`)
      .set('count', `${count || 10}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection);

    const url = `${this.workerUrl}/${workerId}/educationHistory`;
    return this.http.get<IPagedResult<EducationHistoryGridDto>>(url, { params: params });
  }

  getWorkerEducationHistoryRecordById(workerId: number, educationHistoryRecordId: number): Observable<EducationHistoryRecordDto> {
    const url = `${this.workerUrl}/${workerId}/educationHistory/${educationHistoryRecordId}`;
    return this.http.get<EducationHistoryRecordDto>(url);
  }

  getWorkerEducationDegree(workerId: number) {
    const url = `${this.workerUrl}/${workerId}/educationHistory/degree`;
    return this.http.get<number>(url);
  }

  getWorkerDataForAgreementForm(workerIds: number[]): Observable<WorkerDataForAgreementFormDto[]> {
    const url = `${this.workerUrl}/workersDataForAgreementForm`;

    let params = new HttpParams();
    workerIds.forEach((id) => {
      params = params.append('workerIds', id);
    });

    return this.http.get<WorkerDataForAgreementFormDto[]>(url, { params });
  }

  getWorkerProfile(workerId: number): Observable<WorkerProfile> {
    const url = `${this.workerUrl}/workerProfile/${workerId}`;
    return this.http.get<WorkerProfile>(url);
  }

  getWorkerLegalizationVisibility(workerId: number): Observable<boolean> {
    const url = `${this.workerPath}/${workerId}/hasLegalization`;
    return this.cachedHttp.get<boolean>(url);
  }

  getWorkerProfileStatementsSummary(workerId: number): Observable<WorkerFormStatementSummary[]> {
    const url = `${this.workerUrl}/workerProfile/${workerId}/statementsSummary`;
    return this.http.get<WorkerFormStatementSummary[]>(url);
  }

  getWorkerProfileDocuments(workerId: number): Observable<WorkerProfileDocumentsDto> {
    const url = `${this.workerUrl}/workerProfile/${workerId}/documents`;
    return this.http.get<WorkerProfileDocumentsDto>(url);
  }

  getWorkerFormQuestions(workerFormId: number): Observable<FormQuestion[]> {
    const url = `${this.workerUrl}/formQuestions/${workerFormId}`;
    return this.http.get<FormQuestion[]>(url);
  }

  getWorkerFullName(workerId: number): Observable<string> {
    const url = `${this.workerUrl}/fullName/${workerId}`;
    return this.http.get(url, { responseType: 'text' });
  }

  getWorkerFamilyMembers(
    workerId: number,
    page: number = 1,
    count: number = 20,
    sortingField: string = 'lastName',
    sortingDirection: string = 'desc',
    kinshipDegreeId?: number,
    maxAge?: number,
  ): Observable<IPagedResult<WorkerFamilyMemberGridDto>> {
    let params = new HttpParams()
      .set('page', `${page}`)
      .set('count', `${count}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection);

    if (kinshipDegreeId) {
      params = params.set('kinshipDegreeId', kinshipDegreeId);
    }
    if (maxAge) {
      params = params.set('maxAge', maxAge);
    }

    const url = `${this.workerUrl}/${workerId}/familyMembers`;
    return this.http.get<IPagedResult<WorkerFamilyMemberGridDto>>(url, { params: params });
  }

  getWorkerFamilyMemberById(workerId: number, memberId: number): Observable<WorkerFamilyMemberDto> {
    const url = `${this.workerUrl}/${workerId}/familyMembers/${memberId}`;
    return this.http.get<WorkerFamilyMemberDto>(url);
  }

  getWorkerIdByAuthServerUserId(authServerUserId: string): Observable<number> {
    const url = `${this.workerPath}/getWorkerIdByAuthServerUserId/${authServerUserId}`;
    return this.cachedHttp.get<number>(url);
  }

  getWorkerSettings(workerId: number): Observable<WorkerSettingsDto> {
    const url = `${this.workerUrl}/${workerId}/settings`;

    return this.http.get<WorkerSettingsDto>(url);
  }

  getWorkerProfileAgreementsPageConfig(workerId: number): Observable<WorkerProfileAgreementsPageConfigDto> {
    const url = `${this.workerUrl}/${workerId}/getWorkerProfileAgreementsPageConfig`;
    return this.http.get<WorkerProfileAgreementsPageConfigDto>(url);
  }

  getInternalWorkerAgreements(workerId: number): Observable<InternalWorkerAgreementDto[]> {
    const url = `${this.workerUrl}/${workerId}/internalWorkerAgreements`;
    return this.http.get<InternalWorkerAgreementDto[]>(url);
  }

  getWorkerContactInfo(workerId: number): Observable<WorkerContactInfoDto> {
    const url = `${this.workerUrl}/${workerId}/contactInfo`;
    return this.http.get<WorkerContactInfoDto>(url);
  }

  insureWorkerFamilyMember(workerId: number, memberId: number, request: InsureWorkerFamilyMemberRequest) {
    const url = `${this.workerUrl}/${workerId}/familyMembers/${memberId}/insure`;
    return this.http.post<any>(url, request);
  }

  deregistrateWorkerFamilyMemberInsurance(workerId: number, memberId: number, request: DeregistrateWorkerFamilyMemberInsurance) {
    const url = `${this.workerUrl}/${workerId}/familyMembers/${memberId}/deregistrateInsurance`;
    return this.http.post<any>(url, request);
  }

  updateWorkerContactInfo(workerId: number, request: WorkerContactInfoDto): Observable<any> {
    const url = `${this.workerUrl}/${workerId}/contactInfo`;
    return this.http.put<any>(url, request);
  }

  saveWorkerFiles(files: WorkerFileDto[]): Observable<SaveWorkerFilesResponseDto> {
    const url = `${this.workerUrl}/saveWorkerFiles`;
    return this.http.put<SaveWorkerFilesResponseDto>(url, files);
  }

  saveLegalizationDocument(legalizationDocumentDto: LegalizationDocumentDto): Observable<any> {
    const url = `${this.workerUrl}/saveLegalizationDocument`;
    return this.http.put<any>(url, legalizationDocumentDto);
  }

  deleteFile(fileId: number): Observable<any> {
    const url = `${this.workerUrl}/deleteFile`;
    const httpParams = new HttpParams().set('workerfileId', fileId.toString());

    return this.http.delete<any>(url, { params: httpParams });
  }

  updateWorkerEmploymentHistoryRecord(
    workerId: number,
    employmentHistoryRecordId: number,
    request: CreateOrUpdateWorkerEmploymentHistoryRecordRequest,
  ) {
    const url = `${this.workerUrl}/${workerId}/employmentHistory/${employmentHistoryRecordId}`;
    return this.http.patch<SaveWorkerFilesResponseDto>(url, request);
  }

  updateWorkerEducationHistoryRecord(workerId: number, educationHistoryRecordId: number, request: CreateOrUpdateWorkerEducationHistoryRecordRequest) {
    const url = `${this.workerUrl}/${workerId}/educationHistory/${educationHistoryRecordId}`;
    return this.http.patch<number>(url, request);
  }

  deleteWorkerEducationHistoryRecord(workerId: number, educationHistoryRecordId: number): Observable<any> {
    const url = `${this.workerUrl}/${workerId}/educationHistory/${educationHistoryRecordId}`;
    return this.http.delete<any>(url);
  }

  updateWorkerEducationDegree(workerId: number, educationDegreeId: number) {
    const url = `${this.workerUrl}/${workerId}/educationHistory/degree/${educationDegreeId}`;
    return this.http.post<number>(url, null);
  }

  updateWorkerFamilyMember(workerId: number, memberId: number, request: CreateOrUpdateWorkerFamilyMemberRequest) {
    const url = `${this.workerUrl}/${workerId}/familyMembers/${memberId}`;
    return this.http.put<number>(url, request);
  }

  updateInternalWorker(workerId: number, internalWorker: InternalWorkerDto): Observable<ApiResult<any>> {
    const url = `${this.workerUrl}/internalworker/${workerId}`;
    return this.http.post<ApiResult<any>>(url, internalWorker);
  }

  updateWorkerSettings(workerId: number, request: WorkerProfileSettingsRequest): Observable<any> {
    const url = `${this.workerUrl}/${workerId}/settings`;
    return this.http.patch<any>(url, request);
  }

  getWorkerCompaniesList(workerId: number): Observable<CompanyDto[]> {
    const url = `${this.workerUrl}/${workerId}/companies`;
    return this.http.get<CompanyDto[]>(url);
  }

  getWorkerAddress(workerId: number): Observable<WorkerAddressResponse> {
    const url = `${this.workerUrl}/${workerId}/address`;
    return this.http.get<WorkerAddressResponse>(url);
  }

  getAccessToRegulationsAndProcedures(workerId: number): Observable<boolean> {
    const url = `${this.workerUrl}/accessToRegulationsAndProcedures/${workerId}`;
    return this.http.get<boolean>(url);
  }

  hasAccessToAdditionalWorkerData(): Observable<boolean> {
    const url = `${this.workerPath}/hasAccessToAdditionalWorkerData`;
    return this.cachedHttp.get<boolean>(url);
  }

  getEmploymentHistorySkipInformation(workerId: number): Observable<EmploymentHistorySkipInformationDto> {
    const url = `${this.workerUrl}/${workerId}/employmentHistorySkipInformation`;
    return this.http.get<EmploymentHistorySkipInformationDto>(url);
  }

  getWorkerEmploymentsWithWorkedDays(workerId: number, pagingParameters: IPagingAndSortingParameters) {
    const url = `${this.workerUrl}/${workerId}/getWorkerEmploymentsWithWorkedDays`;

    let params = new HttpParams()
      .set('page', `${pagingParameters.Page || 1}`)
      .set('count', `${pagingParameters.Count || 10}`)
      .set('sortingField', pagingParameters.SortingField)
      .set('sortingDirection', pagingParameters.SortingDirection);

    return this.http.get<IPagedResult<WorkerEmploymentWithWorkedDaysDto>>(url, { params: params });
  }

  getAvailableInstitutionsForHrNumber(workerId: number): Observable<DictionaryItem[]> {
    const url = `${this.workerUrl}/${workerId}/availableInstitutionsForHrNumber`;
    return this.http.get<DictionaryItem[]>(url);
  }

  getNewestRecruiter(workerId: number, employerId: number): Observable<DictionaryItem> {
    const url = `${this.workerUrl}/${workerId}/${employerId}/newestRecruiter`;
    return this.http.get<DictionaryItem>(url);
  }

  getEducationHistorySkipInformation(workerId: number): Observable<EducationHistorySkipInformationDto> {
    const url = `${this.workerUrl}/${workerId}/educationHistorySkipInformation`;
    return this.http.get<EducationHistorySkipInformationDto>(url);
  }

  getBankAccountNumberConfiguration(workerId: number): Observable<GetBankAccountNumberConfigurationResponse> {
    const url = `${this.workerUrl}/${workerId}/bankAccountNumberConfiguration`;
    return this.http.get<GetBankAccountNumberConfigurationResponse>(url);
  }
}
